import { noop } from '@amzn/storm-ui-utils';
import {
  AcesAjaxMetricPublisher,
  ChromeApiMetricPublisher,
  ChromeBundleMetricPublisher,
  FooterMetricPublisher,
  HeaderMetricPublisher,
  InternalChromeApiMetricPublisher,
  KatalProdOverrideFooterMetricPublisher,
  KatalProdOverrideHeaderMetricPublisher,
  KatalProdOverrideNavMetricPublisher,
  KatalProdOverridePivotNavMetricPublisher,
  NavMetricPublisher,
  PivotNavMetricPublisher,
} from '../commons/utils/metrics';
import { getInternalChromeConfig } from 'src/commons/apis';

type MetricConfiguration = {
  emitMetric: (entry: PerformanceEntry, logType: LogTypes) => void;
  type: LogTypes;
  pattern?: RegExp;
};

type MetricPublisher =
  | typeof ChromeApiMetricPublisher
  | typeof ChromeBundleMetricPublisher
  | typeof FooterMetricPublisher
  | typeof HeaderMetricPublisher
  | typeof InternalChromeApiMetricPublisher
  | typeof NavMetricPublisher
  | typeof PivotNavMetricPublisher;

enum LogTypes {
  Download = 'Download',
  Fetch = 'Fetch',
  Mount = 'Mount',
}

let isInternalRequest: boolean | undefined = undefined;

const HASHED_BUNDLE_DOWNLOAD_METRIC = 'HashedBundleDownloadDuration';
const HASHED_BUNDLE_NETWORK_DOWNLOAD_METRIC =
  'HashedBundleNetworkDownloadDuration';
const HASHED_BUNDLE_BODY_TRANSFER_METRIC = 'HashedBundleBodyTransferDuration';
const HASHED_BUNDLE_CACHE_HIT_METRIC = 'HashedBundleCacheHit';
const HASHED_BUNDLE_ENCODED_BYTES_METRIC = 'HashedBundleEncodedBytes';
const HASHED_BUNDLE_RESPONSE_END_TO_INIT_METRIC =
  'HashedBundleResponseEndToInit';

const isResourceTiming = (
  entry: PerformanceEntry,
): entry is PerformanceResourceTiming => entry.entryType === 'resource';

const emitHashedBundleMetrics = (entry: PerformanceEntry) => {
  ChromeBundleMetricPublisher.publishTimerMonitor(
    HASHED_BUNDLE_DOWNLOAD_METRIC,
    entry.duration,
  );
  if (!isResourceTiming(entry)) {
    return;
  }
  if (entry.responseEnd > 0) {
    ChromeBundleMetricPublisher.publishTimerMonitor(
      HASHED_BUNDLE_RESPONSE_END_TO_INIT_METRIC,
      performance.now() - entry.responseEnd,
    );
  }
  // Cross-origin body sizes read as 0 unless the CDN sends Timing-Allow-Origin,
  // and then a cache hit cannot be told apart from a download.
  if (entry.encodedBodySize === 0) {
    return;
  }
  const isCacheHit = entry.transferSize === 0;
  ChromeBundleMetricPublisher.publishCounterMonitor(
    HASHED_BUNDLE_CACHE_HIT_METRIC,
    isCacheHit ? 1 : 0,
  );
  ChromeBundleMetricPublisher.publishCounterMonitor(
    HASHED_BUNDLE_ENCODED_BYTES_METRIC,
    entry.encodedBodySize,
  );
  if (!isCacheHit) {
    ChromeBundleMetricPublisher.publishTimerMonitor(
      HASHED_BUNDLE_NETWORK_DOWNLOAD_METRIC,
      entry.duration,
    );
    ChromeBundleMetricPublisher.publishTimerMonitor(
      HASHED_BUNDLE_BODY_TRANSFER_METRIC,
      entry.responseEnd - entry.responseStart,
    );
  }
};

const getIsInternalRequest = async () => {
  if (isInternalRequest !== undefined) {
    return isInternalRequest;
  }
  const internalChromeConfig = await getInternalChromeConfig();
  isInternalRequest = internalChromeConfig.isInternalRequest;
  return isInternalRequest;
};

const log = async (entry: PerformanceEntry, type: LogTypes) => {
  try {
    if (await getIsInternalRequest()) {
      console.log(
        `%c[CHROME] ${entry.name} ${type} Duration: ${entry.duration} ms`,
        'color: cadetblue',
      );
    }
  } catch (e: any) {
    console.error('failed to fetch chromeData:', e);
  }
};

const generateDurationMetricsFn =
  (publisher: MetricPublisher, metricName: string) =>
  (entry: PerformanceEntry, logType: LogTypes) => {
    publisher.publishTimerMonitor(metricName, entry.duration);
    log(entry, logType);
  };

const chromeBundleDownloadDurationFunction = generateDurationMetricsFn(
  ChromeBundleMetricPublisher,
  'DownloadDuration',
);

enum MetricCategories {
  InitialLoadMetrics = 'initialLoadMetrics',
  AsyncLoadMetrics = 'asyncLoadMetrics',
}

const cloudfrontCdnDomains =
  /https:\/\/(d3fqcrnfcquh98|d10mnqzgyfbljw|d1djdwhk5taman|ddjnx009imlt4|d1qdmw2uw8q3oy|d15sx2w9gv56iq|d2cml4dovpy1xa|d1p0fikz5ktgsl|d14dq4vdy1a3zd|d2ybzpzm1pr0am|db75jln3aqw6e|d1eydj7jwazt8k)\.cloudfront\.net/;
// used to continue emitting existing metrics for
// MTR dashboard until we switch over to cached bundles
const mainBundleRegex = /\/main.js/;
const matchAllFiles = /.*\.js/;
const hashedChromeBundleRegex =
  /\/.+-cacheable\/(main|aacchromeui-standalone)\..+\.js$/;

const resourcesToLog: MetricConfiguration[] = [
  {
    // captures https://d1p0fikz5ktgsl.cloudfront.net/main.js to continue emitting
    // legacy latency metrics.  This will be depreacted after migrating to
    // measuring the download duration per js bundle
    pattern: new RegExp(cloudfrontCdnDomains.source + mainBundleRegex.source),
    type: LogTypes.Download,
    emitMetric: chromeBundleDownloadDurationFunction,
  },
  {
    // Emit duration for all files from our cloudfront domains with the file names
    // allowing our team to look up the download times per JS bundle
    pattern: new RegExp(cloudfrontCdnDomains.source + matchAllFiles.source),
    type: LogTypes.Download,
    emitMetric: (entry: PerformanceEntry, _) => {
      ChromeBundleMetricPublisher.publishTimerMonitor(
        entry.name,
        entry.duration,
      );
    },
  },
  {
    // emit hashed bundle download duration
    pattern: new RegExp(
      cloudfrontCdnDomains.source + hashedChromeBundleRegex.source,
    ),
    type: LogTypes.Download,
    emitMetric: emitHashedBundleMetrics,
  },
];

const metricsToLog: Record<
  MetricCategories,
  Record<string, MetricConfiguration>
> = {
  initialLoadMetrics: {
    'aac-chrome-api': {
      type: LogTypes.Mount,
      emitMetric: generateDurationMetricsFn(
        ChromeApiMetricPublisher,
        'Initialization',
      ),
    },
    'aac-chrome-internal-api': {
      type: LogTypes.Mount,
      emitMetric: generateDurationMetricsFn(
        InternalChromeApiMetricPublisher,
        'Initialization',
      ),
    },
  },
  asyncLoadMetrics: {
    'aac-chrome-ui-header': {
      type: LogTypes.Mount,
      emitMetric: generateDurationMetricsFn(
        HeaderMetricPublisher,
        'InitialRenderTime',
      ),
    },
    'aac-chrome-ui-footer': {
      type: LogTypes.Mount,
      emitMetric: generateDurationMetricsFn(
        FooterMetricPublisher,
        'InitialRenderTime',
      ),
    },
    'aac-chrome-ui-nav': {
      type: LogTypes.Mount,
      emitMetric: generateDurationMetricsFn(
        NavMetricPublisher,
        'InitialRenderTime',
      ),
    },
    'aac-chrome-ui-pivot-nav': {
      type: LogTypes.Mount,
      emitMetric: generateDurationMetricsFn(
        PivotNavMetricPublisher,
        'InitialRenderTime',
      ),
    },
    'aac-chrome-ui-header-prod-override': {
      type: LogTypes.Mount,
      emitMetric: generateDurationMetricsFn(
        KatalProdOverrideHeaderMetricPublisher,
        'InitialRenderTime',
      ),
    },
    'aac-chrome-ui-footer-prod-override': {
      type: LogTypes.Mount,
      emitMetric: generateDurationMetricsFn(
        KatalProdOverrideFooterMetricPublisher,
        'InitialRenderTime',
      ),
    },
    'aac-chrome-ui-nav-prod-override': {
      type: LogTypes.Mount,
      emitMetric: generateDurationMetricsFn(
        KatalProdOverrideNavMetricPublisher,
        'InitialRenderTime',
      ),
    },
    'aac-chrome-ui-pivot-nav-prod-override': {
      type: LogTypes.Mount,
      emitMetric: generateDurationMetricsFn(
        KatalProdOverridePivotNavMetricPublisher,
        'InitialRenderTime',
      ),
    },
    '/communications/placements/globalBanner': {
      type: LogTypes.Fetch,
      emitMetric: generateDurationMetricsFn(
        AcesAjaxMetricPublisher,
        'GlobalBanner',
      ),
    },
    '/communications/placements/drawer': {
      type: LogTypes.Fetch,
      emitMetric: generateDurationMetricsFn(
        AcesAjaxMetricPublisher,
        'NotificationDrawer',
      ),
    },
    '/communications/drawerStrings': {
      type: LogTypes.Fetch,
      emitMetric: generateDurationMetricsFn(
        AcesAjaxMetricPublisher,
        'NotificationDrawerStrings',
      ),
    },
    '/ccx/ajax/navState': {
      type: LogTypes.Fetch,
      emitMetric: generateDurationMetricsFn(
        InternalChromeApiMetricPublisher,
        'NavState',
      ),
    },
  },
};

const handlePerformanceObserver = (list: PerformanceObserverEntryList) => {
  const entries = list.getEntries();

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (
      entry instanceof PerformanceResourceTiming &&
      entry.initiatorType === 'fetch'
    ) {
      // because the url for aces banners include the entity id
      // in the query string, we can only reliably match on the
      // the pathname
      const url = new URL(entry.name);
      const ajaxResourceToLog = metricsToLog.asyncLoadMetrics[url.pathname];
      if (!ajaxResourceToLog) {
        continue;
      }
      ajaxResourceToLog.emitMetric(entry, LogTypes.Fetch);
    } else if (entry instanceof PerformanceMeasure) {
      const shouldOverrideProd =
        entry.name.includes('aac-chrome-ui') &&
        (window.location.hostname.includes('advertising.amazon') ||
          window.location.hostname.includes('advertising-japan.amazon'));
      const measureTimingEntry =
        metricsToLog.asyncLoadMetrics[
          `${entry.name}${shouldOverrideProd ? '-prod-override' : ''}`
        ];
      if (!measureTimingEntry) {
        continue;
      }
      measureTimingEntry.emitMetric(entry, LogTypes.Mount);
      window.performance.clearMeasures(entry.name);
    }
  }
};

const initializePerformanceObserver = () => {
  const performanceObserver = new PerformanceObserver(
    handlePerformanceObserver,
  );
  // only handle measure and resource events from the performance api
  performanceObserver.observe({ entryTypes: ['measure', 'resource'] });
};

const emitInitialLoadMetrics = () => {
  const configurationList = Object.entries(metricsToLog.initialLoadMetrics);
  for (const [name, config] of configurationList) {
    const foundEntries = window.performance.getEntriesByName(name); // returns an array
    const entry = foundEntries?.length === 1 ? foundEntries[0] : undefined;
    if (!entry) {
      continue;
    }

    config.emitMetric(entry, config.type);

    // remove measure from buffer after logging
    if (entry.entryType === 'measure') {
      window.performance.clearMeasures(entry.name);
    }
  }
};

const emitResourcesMetrics = () => {
  const resources = window.performance.getEntriesByType('resource');
  let numberOfLoggedEntries = 0;
  for (const entry of resources) {
    for (const config of resourcesToLog) {
      if (!config.pattern || !config.pattern.test(entry.name)) {
        continue;
      }
      config.emitMetric(entry, config.type);
      numberOfLoggedEntries += 1;
    }
    if (numberOfLoggedEntries >= resourcesToLog.length) {
      break;
    }
  }
};

export const performanceMark = (markName: string) => {
  if (
    !window.performance ||
    typeof window.performance.mark !== 'function' ||
    typeof window.performance.measure !== 'function'
  ) {
    return noop;
  }
  const markStartName = `${markName}-start`;
  window.performance.mark(markStartName);
  return () => {
    window.performance.measure(markName, markStartName);
    // remove mark fomr buffer after measurings
    window.performance.clearMarks(markStartName);
  };
};

export const initPerformanceLogger = () => {
  // do nothing if performance api is not available
  if (!window.performance) {
    return;
  }

  // when the page loads, there are already metrics logged into
  // the performance buffer.  This function logs these to katal
  // first before adding the performance observer.
  emitInitialLoadMetrics();

  emitResourcesMetrics();

  // add the performance observer to log ajax request metrics
  initializePerformanceObserver();
};
